<template lang="pug">
div
  LfHeaderTitleComponent(:title="texts.headerTitle" class="header-title")
  LfTotalsComponent(
    v-if="totals && totals.length"
    :items="totals"
  )
  GridTableComponent(
    v-if="renderGrid && !showSpinnerLayer"
    :itemsData="isPortalUser ? listItems : serverSideData"
    :gridConfiguration="listConfig"
    :listName="listName"
    :toolbarOptions="customToolbarOptions"
    :contextMenuItems="invoiceContextMenuItems"
    :context="context"
    :archivedFilter="archivedSelectedOption"
    :disabledGridCellClicked="getDisableGridProperty"
    :activeOnClickCell="selectedOnlyOneInvoice"
    :checkboxSelectColumn="showCheckboxSelectColumn"
    :showFirstColumn="showCheckboxSelectColumn"
    :frozenColumns="isPortalUser ? 0 : 2"
    :commandClick="onCommandClick"
    @contextMenuClicked="onContextMenuClicked"
    @contextMenuBeforeOpen="onContextMenuBeforeOpen"
    @toolbarClicked="onToolbarClicked"
    @rectifyCustomerInvoiceEvent="rectifyCustomerInvoice"
    @selectedRecords="onSelectedRecords"
    @customToolbarContextMenuClick="onCustomToolbarContextMenuClick"
    :excelExportedFileName="fileName"
    ref="grid"
    @gridActionChange="gridActionChange"
    @gridExportData="gridExportData"
    :useServerPagination="useServerPagination"
    :usePersistSelection="usePersistSelection"
    :itemsDataExport="serverSideExportData"
    @cellClicked="onCellClicked"
  )

  PrintOrSaveInvoicesDialog(
    v-if="renderPrintOrSaveInvoicesDialog"
    :showDialog="showTemplateDialog"
    :idEntityType="idEntityType"
    @closePrintOrSaveInvoiceDialog="closeTemplateDialog"
    @generateTemplateDialog="generateTemplateDialog"
  )

  TemplateSelectorComponent(
    v-if="renderTemplateSelectorComponent"
    :showDialog="showTemplateDialog"
    :idEntityType="idEntityType"
    @closeTemplateDialog="closeTemplateDialog"
    @generateTemplateDialog="generateTemplateDialog"
  )

  FacturaEDialog(
    v-if="getShowFacturaEDialog"
    :key="getFacturaEDialogKey"
    :showDialog="showFacturaEDialog"
    :invoiceId="getSelectedInvoiceId"
    :invoiceDescription="getSelectedInvoiceDescription"
    :invoiceTemplates="hasProfessionalFacturaETemplate"
    @closeDialog="closeFacturaEDialog"
  )
</template>

<script lang="ts">
import { mixins } from 'vue-class-component'
import { Component } from 'vue-property-decorator'
import GridTableComponent from '@/components/grids/GridTable/GridTableComponent.vue'
import { ContextName, ModuleNamespaces } from '@/store/types/storeGlobalTypes'
import ListViewMixin from '@/mixins/ListViewMixin.vue'
import ArchivedFilterMixin from '@/mixins/ArchivedFilterMixin.vue'
import PortalUserMixin from '@/mixins/PortalUserMixin.vue'
import { Action, Getter, Mutation } from 'vuex-class'
import { ListNames } from '@/store/modules/configuration/configurationTypes'
import { BillingTypes, TotalItem } from '@/store/modules/billing/billingTypes'
import PrintInvoiceContextMenuMixin from '@/mixins/PrintInvoiceContextMenuMixin.vue'
import { entity } from '@/store/modules/entities/entitiesTypes'
import PrintOrSaveInvoicesDialog from '@/components/billing/PrintOrSaveInvoicesDialog/PrintOrSaveInvoicesDialog.vue'
import { Icons } from '@/icons/icons'
import { URLS } from '@/router/routes/urlRoutes'
import { UserType } from '@/store/modules/auth/authTypes'
import { ComponentWhereIsRendered } from '@/store/modules/alerts/alertsTypes'
import { InvoiceEntityId } from '@/general/entityIds'
import { MenuItemModel } from '@syncfusion/ej2-vue-navigations'
import { DialogTypes } from '@/store/modules/dialog/dialogTypes'
import { InvoiceGenerated, InvoiceStatus, InvoiceTypesNames } from '@/store/modules/invoices/invoicesTypes'
import { MAX_SELECTED_INVOICE_PRINT } from '@/components/billing/PrintOrSaveInvoicesDialog/types/PrintOrSaveInvoicesDialogTypes'
import InvoicesToolBarMixin from '@/mixins/InvoicesToolBarMixin.vue'
import LfHeaderTitleComponent from '@/components/HeaderTitle/LfHeaderTitleComponent.vue'
import LfTotalsComponent from '@/components/LfTotals/LfTotalsComponent.vue'
import { ActionName, CommandClickEventArgs, CommandModel } from '@/components/grids/LfGrid/LfGridTypes'
import { gridConfigurationWithActionColumn } from '@/helpers/grid'
import {
  columnsChooserToolbarItem,
  filterToolbarItem,
  searchToolbarItem
} from '@/components/grids/LfGrid/components/ToolbarContextMenu/ToolbarContextMenuType'
import { ContextMenuClickEventArgs, DataResult } from '@syncfusion/ej2-vue-grids'
import useGridSaveUserConf from '@/composables/useGridSaveUserConf'
import { formatFieldsTypeDateEPOCH } from '@/helpers/dateTime'
import { TrackerEvents, trackEvent } from '@/plugins/tracker'
import { resetLoadedCustomMultiselectFlags } from '@/components/grids/CustomFilters'
import TemplateSelectorComponent from '@/components/template/TemplateSelectorComponent/TemplateSelectorComponent.vue'
import { InvoiceMaxNumberFormFields } from '@/mixins/InvoicesMaxNumberFormFieldsLogic/types/InvoicesMaxNumberFormFieldsLogicMixinTypes'
import FacturaEDialog from '@/components/billing/FacturaEDialog/FacturaEDialog.vue'
import { FacturaEStatusEnum } from '@/store/modules/facturaE/facturaETypes'
import { isTrue } from '@/helpers/boolean'
import useManProfessionals from '@/views/ConfigurationPanel/composables/useManProfessionals'

const authModule = ModuleNamespaces.AUTH
const billingModule: string = ModuleNamespaces.BILLING
const dashboardWidgetsModule = ModuleNamespaces.DASHBOARD_WIDGETS
const entitiesModule: string = ModuleNamespaces.ENTITIES
const invoicesModule = ModuleNamespaces.INVOICES
const configurationModule = ModuleNamespaces.CONFIGURATION

const { parseUserConfig } = useGridSaveUserConf()

@Component({
  components: {
    FacturaEDialog,
    TemplateSelectorComponent,
    GridTableComponent,
    LfHeaderTitleComponent,
    LfTotalsComponent,
    PrintOrSaveInvoicesDialog
  }
})
export default class CustomerInvoicesView extends mixins(
  ListViewMixin,
  ArchivedFilterMixin,
  PrintInvoiceContextMenuMixin,
  PortalUserMixin,
  InvoicesToolBarMixin
) {
  @Action('rememberUserCustomConfigurationList', { namespace: configurationModule })
  rememberUserCustomConfigurationList: (flag: boolean) => {}

  @Action('fetchCustomerInvoiceList', { namespace: billingModule })
  fetchCustomerInvoiceList: (filter: {}) => Promise<void>

  @Action('fetchCustomerInvoiceTotals', { namespace: billingModule })
  fetchCustomerInvoiceTotals: (filter: {}) => Promise<void>

  @Action('saveSelectedIdEntityType', { namespace: entitiesModule })
  saveSelectedIdEntityType: (type: number) => void

  @Action('rectifyCustomerInvoice', { namespace: invoicesModule })
  rectifyCustomerInvoiceAction: ({}) => Promise<string>

  @Action('fetchInvoices', { namespace: invoicesModule })
  fetchPortalInvoices: (filter: {}) => []

  @Action('billingRemove', { namespace: billingModule })
  removeCustomerInvoice: (url: string) => { success: boolean; message: string }

  @Action('toggleComeFromDashboardWidgetFlag', { namespace: dashboardWidgetsModule })
  toggleComeFromDashboardWidgetFlagAction: (status: boolean) => Promise<void>

  @Getter('getBillingData', { namespace: billingModule })
  invoiceData: []

  @Getter('getTotals', { namespace: billingModule })
  totals: TotalItem[]

  @Getter('getCompanyTemplateInvoices', { namespace: authModule })
  hasCompanyTemplateInvoice: boolean

  @Mutation('ADD_GENERATE_INVOICE', { namespace: invoicesModule })
  addGenerateInvoice: (selectedInvoices: InvoiceGenerated[]) => void

  facturaEDialogKey: number = 0
  showFacturaEDialog: boolean = false
  useServerPagination: boolean = true
  usePersistSelection: boolean = true
  showCheckboxSelectColumn: boolean = true

  serverSideData: DataResult = {} as DataResult
  serverSideExportData = []

  idContextMenu: string = 'customToolbarContextMenuComponent'

  idEntity: number | string = 0
  idEntityType: number = InvoiceEntityId.CUSTOMER_INVOICES
  gridFilters: any = { page: 0, pageSize: 10, columnOrder: [] }

  fileName = InvoiceTypesNames.CUSTOMER
  selectedInvoices = []

  texts = {
    edit: this.$t('components.context_menu.edit'),
    headerTitle: this.$t('views.billing_customer_invoices.title').toString(),
    openNewWindow: this.$t('components.context_menu.open_window'),
    printButton: this.$t('components.context_menu.generate_invoice'),
    rectifyCustomerInvoice: this.$t('components.context_menu.rectify_customer_invoice'),
    sendFacturaE: this.$t('components.context_menu.send_facturae')
  }

  checkColumnSelected: boolean = false
  facturaEActive: boolean = false

  get getFacturaEDialogKey(): number {
    return this.facturaEDialogKey
  }

  get renderPrintOrSaveInvoicesDialog(): boolean {
    return this.hasCompanyTemplateInvoice && !this.isPortalUser
  }

  get renderTemplateSelectorComponent(): boolean {
    return this.hasCompanyTemplateInvoice && this.isPortalUser
  }

  renderNewFacturaEDialog(): void {
    if (this.selectedInvoices.length !== 1 || isNaN(this.getSelectedInvoiceId)) {
      this.showDialogAction({
        type: DialogTypes.WARNING,
        message: this.$t('components.dialog.facturae_missing'),
        mainButtonText: this.$t('action_buttons.accept')
      })
      return
    }

    this.showDialogAction({
      type: DialogTypes.INFO,
      message: this.$t('components.dialog.facturae_prompt', { description: this.getSelectedInvoiceDescription }),
      action: async () => {
        this.facturaEDialogKey += 1
        this.showFacturaEDialog = true
      },
      mainButtonText: this.$t('action_buttons.issue_and_send'),
      secondaryButtonText: this.$t('action_buttons.cancel')
    })
  }

  /** @override **/
  get printButton() {
    return {
      iconCss: this.isPortalUser ? Icons.OPEN_WINDOW : Icons.DOCUMENT_VALIDATE,
      text: this.isPortalUser ? this.texts.openNewWindow : this.texts.printButton
    }
  }

  get listConfig() {
    const config = JSON.parse((this as any).listConfiguration['Config'])
    const index = config.columns.findIndex(({ editType }: any) => editType === 'booleanedit')
    if (index && index !== -1) {
      config.columns[index].visible = this.showCheckboxSelectColumn
    }
    const commandButtons: CommandModel[] = [
      {
        id: ActionName.OPEN_KEBAK_MENU,
        type: 'None',
        title: 'Open',
        buttonOption: {
          iconCss: Icons.KEBAB,
          cssClass: 'custombutton'
        }
      }
    ]
    const gridConfiguration = gridConfigurationWithActionColumn(config, commandButtons)
    return JSON.stringify(gridConfiguration)
  }

  get listName() {
    return (this as any).listConfiguration['Alias']
  }

  get selectedOnlyOneInvoice() {
    return this.selectedInvoices.length <= 1
  }

  get context() {
    return ContextName.BILLING
  }

  get invoiceContextMenuItems() {
    if (this.isPortalUser) {
      return [this.printButton]
    }

    const items = this.contextMenuItemsDefault
    const getPrintContextMenuItems = this.getPrintContextMenuItems(items)
    const { canSave } = this.checkEntityPermissionsGetter(InvoiceEntityId.CUSTOMER_INVOICES)
    if (canSave) {
      getPrintContextMenuItems.splice(3, 0, {
        text: this.texts.rectifyCustomerInvoice,
        iconCss: Icons.RECTIFY_CUSTOMER_INVOICE,
        action: this.showDialog
      })
      getPrintContextMenuItems.splice(3, 0, {
        id: ActionName.SEND_FACTURA_E,
        text: this.texts.sendFacturaE,
        iconCss: Icons.DOCUMENT_VALIDATE
      })
    }
    return getPrintContextMenuItems
  }

  get filters() {
    const filters = {
      ...this.gridFilters,
      archived: this.archivedSelectedOption
    }
    return this.isPortalUser ? { ...filters, ...this.portalFilters } : filters
  }

  get customToolbarOptions() {
    const { filters } = this.toolbarOptionsTooltipTexts
    return [
      searchToolbarItem,
      filterToolbarItem,
      ...(!this.isPortalUser
        ? [
            {
              id: 'add',
              text: this.$t('components.grid_table.tooltip.new_customer_invoice'),
              tooltipText: this.$t('components.grid_table.tooltip.new_customer_invoice'),
              cssClass: 'lf-btn-model2',
              align: 'Right'
            }
          ]
        : []),
      columnsChooserToolbarItem,
      {
        id: ActionName.CUSTOM_TOOLBAR_CONTEXT_MENU,
        align: 'Right',
        cssClass: 'lf-btn-kebak',
        prefixIcon: Icons.KEBAB,
        contextMenuItems: [
          ...(!this.isPortalUser
            ? [
                {
                  id: ActionName.ARCHIVED,
                  iconCss: Icons.FILTER,
                  text: filters,
                  items: this.selectArchivedOption
                }
              ]
            : []),
          {
            id: ActionName.PRINT,
            iconCss: Icons.PRINT,
            text: this.$t('components.grid_table.tooltip.print').toString()
          },
          {
            id: ActionName.CUSTOM_TOOLBAR_CONTEXT_MENU,
            iconCss: Icons.DOWNLOAD,
            text: this.$t('components.grid_table.tooltip.export').toString(),
            items: this.invoicesExportContextMenuItems
          },
          ...(this.facturaEActive
            ? [
                {
                  id: ActionName.DOWNLOAD_B2B_CERTIFICATE,
                  iconCss: Icons.DOWNLOAD,
                  text: this.$t('components.grid_table.tooltip.download_b2b_certificate').toString()
                }
              ]
            : [])
        ]
      }
    ]
  }

  get getShowFacturaEDialog(): boolean {
    return this.showFacturaEDialog
  }

  get getSelectedInvoiceId(): number {
    return (this.selectedInvoices[0] as any).idInvoice
  }

  get getSelectedInvoiceDescription(): string {
    return (this.selectedInvoices[0] as any).description
  }

  get hasProfessionalFacturaETemplate(): boolean {
    return isTrue((this.selectedInvoices[0] as any)?.professionalConfig?.facturaETemplate)
  }

  async created() {
    this.rememberUserCustomConfigurationList(true)

    await this.fetchCurrentListConfiguration(ListNames.CUSTOMER_INVOICES)
    await this.getFilterParams()
    if (this.gridFilters.archived >= 0) {
      this.archivedSelectedOption = parseInt(this.gridFilters.archived) || 0
      this.changeContextMenuIcons(this.archivedSelectedOption, this.idContextMenu)
    }

    await this.fetchGridItems()
    await this.fetchInvoiceTotals()
    if (!this.isPortalUser) {
      await this.checkFacturaEActive()
    }
    this.saveSelectedEntityName(BillingTypes.CUSTOMER_INVOICES)
    this.renderGrid = true
    this.hideSpinnerLayerAction()
  }

  async checkFacturaEActive() {
    try {
      const { factura_e_active } = useManProfessionals()
      const { active } = await factura_e_active()
      this.facturaEActive = active
    } catch (_error) {
      this.facturaEActive = false
    }
  }

  beforeDestroy() {
    resetLoadedCustomMultiselectFlags([InvoiceMaxNumberFormFields.SEQUENCE])
    this.toggleComeFromDashboardWidgetFlagAction(false)
  }

  async fetchGridItems() {
    if (this.isPortalUser) {
      await this.fetchPortalInvoices({
        archived: this.invoicesDefaulArchivedOption,
        userType: UserType.PORTAL,
        selectedRegisterId: this.portalCustomerId,
        listName: ListNames.LIST_CUSTOMER_INVOICES
      })
      return
    }
    await this.fetchCustomerInvoiceList(this.filters)
    this.serverSideData = this.formatDataPaginated(this.invoiceData)
  }

  async fetchInvoiceTotals() {
    await this.fetchCustomerInvoiceTotals(this.filters)
  }

  async getFilterParams() {
    const listConfig = JSON.parse((this as any).listConfiguration['Config'])
    this.archivedSelectedOption = listConfig.archived || 0
    this.gridFilters = await parseUserConfig(listConfig)
  }

  onContextMenuClicked(args: any, selectedRegister: any) {
    if (args.item.id === ActionName.EDIT || args.item.id === ActionName.OPEN_NEW_TAB) {
      trackEvent(TrackerEvents.EDIT_INVOICE)
    }
    const { rowInfo } = args
    const { rowData } = rowInfo || { rowData: selectedRegister }

    if (args.item.id === ActionName.SEND_FACTURA_E) {
      this.renderNewFacturaEDialog()
      return
    }

    const isPrintButton =
      args.item.text === this.texts.printButton || (this.isPortalUser && args.item.text === this.texts.openNewWindow)

    if (isPrintButton && this.selectedInvoices.length > MAX_SELECTED_INVOICE_PRINT) {
      this.showDialog({
        type: DialogTypes.WARNING,
        hideSecondaryButton: true,
        message: this.$t('components.dialog.exceeded_number_of_selected_invoices', {
          selectedInvoicesLength: this.selectedInvoices.length,
          maxInvoiceSelected: MAX_SELECTED_INVOICE_PRINT
        })
      })
      args.cancel = true
      return
    } else if (isPrintButton && this.hasCompanyTemplateInvoice && this.isPortalUser) {
      this.showTemplateDialog = this.checkIfOnlyOneInvoice(rowData.id, this.idEntityType)
      return
    } else if (isPrintButton && this.hasCompanyTemplateInvoice && !this.isPortalUser) {
      this.showTemplateDialog = true
      const selectedInvoices: InvoiceGenerated[] = this.selectedInvoices.map(({ idInvoice, numberInvoice }) => {
        return {
          idEntity: idInvoice,
          idEntityType: this.idEntityType,
          numberInvoice,
          status: InvoiceStatus.LOADING
        }
      })
      this.addGenerateInvoice(selectedInvoices)
    } else if (isPrintButton && !this.hasCompanyTemplateInvoice) {
      // Estos no pueden imprimir más de una factura
      this.printInvoiceOld(rowData.id, entity.invoices.customers.alias)
    }

    if (args.item.id === ActionName.REMOVE) {
      args.cancel = true
      this.confirmDelete(rowData.id, rowData.description)
    }
  }

  generateTemplateDialog(templateSelected: any) {
    this.printInvoice(templateSelected, this.idEntity, this.idEntityType, ComponentWhereIsRendered.GRID_TABLE)
  }

  onToolbarClicked(args: any) {
    const target = args.originalEvent.target.closest('button')
    if (target && target.id === 'archived') {
      this.toggleArchivedContextMenu(args.originalEvent, this.idContextMenu)
      args.cancel = true
    }
    const { item } = args
    if (item.id === ActionName.ADD) {
      trackEvent(TrackerEvents.CREATE_INVOICE)
    }
  }

  async onInvoicesArchivedClick(args: any) {
    this.archivedSelectedOption = args.item.actionType
    this.changeContextMenuIcons(args.item.actionType, this.idContextMenu)
    await this.saveConfig(this.archivedSelectedOption)
    await this.fetchGridItems()
    await this.fetchInvoiceTotals()
  }

  async rectifyCustomerInvoice(selectedInvoiceId: number) {
    const returnedId = await this.rectifyCustomerInvoiceAction(selectedInvoiceId)
    await this.fetchGridItems()

    const routeData = this.$router.resolve({
      name: `${URLS.BILLING}-${URLS.CUSTOMER_INVOICES}`,
      params: { selectedRegisterId: returnedId }
    })

    window.open(routeData.href, '_blank')
  }

  onSelectedRecords(selectedItems: any, checkColumnSelected: boolean) {
    this.checkColumnSelected = checkColumnSelected
    this.selectedInvoices = Array.isArray(selectedItems) ? selectedItems : ([] as any)
  }

  onContextMenuBeforeOpen(contextMenuModule: any, contextMenuGridObject: any, _selectedRegister: any) {
    contextMenuGridObject.items.forEach((item: MenuItemModel) => {
      const enable = !(item.text !== this.texts.printButton && !this.selectedOnlyOneInvoice)
      contextMenuModule.enableItems([item.text], enable)
      contextMenuGridObject.enableItems([item.text], enable)
    })

    const selectedInvoice = this.selectedInvoices[0] as any

    if (FacturaEStatusEnum.DRAFT === selectedInvoice.statusFacturaE) {
      contextMenuModule.showItems([this.$t('action_buttons.remove')])
      contextMenuGridObject.showItems([this.$t('action_buttons.remove')])
    } else {
      contextMenuModule.hideItems([this.$t('action_buttons.remove')])
      contextMenuGridObject.hideItems([this.$t('action_buttons.remove')])
    }

    if ([FacturaEStatusEnum.DRAFT, FacturaEStatusEnum.ERROR].includes(selectedInvoice.statusFacturaE)) {
      contextMenuModule.showItems([this.texts.sendFacturaE])
      contextMenuGridObject.showItems([this.texts.sendFacturaE])
    } else {
      contextMenuModule.hideItems([this.texts.sendFacturaE])
      contextMenuGridObject.hideItems([this.texts.sendFacturaE])
    }
  }

  closeFacturaEDialog(hasValidatorErrors: boolean) {
    this.showFacturaEDialog = false
    if (!hasValidatorErrors) {
      this.refreshGrid()
    }
  }

  refreshGrid() {
    const gridTable = this.$refs.gridTable as any
    if (gridTable && typeof gridTable.refresh === 'function') {
      gridTable.refresh()
    } else {
      this.fetchGridItems()
    }
  }

  closeTemplateDialog() {
    this.showTemplateDialog = false
  }

  onCommandClick({ commandColumn, rowData }: CommandClickEventArgs) {
    const action = commandColumn?.id
    const { id, description } = rowData as any
    switch (action) {
      case ActionName.REMOVE:
        this.confirmDelete(id, description)
        break
      case ActionName.RECTIFY_INVOICE:
        this.rectifyCustomerInvoice(id)
        break
      case ActionName.GENERATE_INVOICE:
        this.print(id)
        break
      case ActionName.OPEN_NEW_TAB:
        this.open(id, '_blank')
        break
    }
  }

  open(id: string, target = '_self') {
    const routeData = this.$router.resolve({
      name: `${URLS.BILLING}-${URLS.CUSTOMER_INVOICES}`,
      params: { selectedRegisterId: id }
    })
    if (routeData && target === '_blank') {
      window.open((routeData as any).href, '_blank')
    } else {
      this.$router.push((routeData as any).resolved)
    }
  }

  print(id: string) {
    if (!this.hasCompanyTemplateInvoice) {
      this.idEntity = id
      this.showTemplateDialog = true
      const selectedInvoices: InvoiceGenerated[] = this.selectedInvoices.map(({ idInvoice, numberInvoice }) => {
        return {
          idEntity: idInvoice,
          idEntityType: this.idEntityType,
          numberInvoice,
          status: InvoiceStatus.LOADING
        }
      })
      this.addGenerateInvoice(selectedInvoices)
    } else {
      // Estos no pueden imprimir más de una factura
      this.printInvoiceOld(id, entity.invoices.customers.alias)
    }
  }

  confirmDelete(id: string, name: string) {
    this.showDialogAction({
      type: DialogTypes.INFO,
      message: this.$t('components.dialog.remove_register_text', {
        register: name,
        text: this.$t('components.dialog.invoices_literal')
      }),
      action: async () => {
        await this.remove(id, name)
        ;(this as any).$refs.grid.refresh()
      },
      mainButtonText: this.$t('action_buttons.remove'),
      secondaryButtonText: this.$t('action_buttons.cancel')
    })
  }

  async remove(id: string, name: string) {
    const url = `invoices/customers/${id}/delete`
    const { success } = await this.removeCustomerInvoice(url)
    if (success) {
      this.showDialogAction({
        type: DialogTypes.SUCCESS,
        message: this.$t('components.dialog.success_removed_register', {
          text: this.$t('components.dialog.invoices_literal'),
          register: name
        })
      })
    } else {
      this.showDialogAction({
        type: DialogTypes.ERROR,
        message: this.$t('components.dialog.error_remove_invoice')
      })
    }
  }

  onCustomToolbarContextMenuClick(args: ContextMenuClickEventArgs) {
    const { item } = args
    const { id } = item

    if (id === ActionName.EXPORT_SAGE) {
      const data = this.getSageFilterData(this.filters, this.archivedSelectedOption, this.selectedInvoices)
      this.downloadExcelSage(data)
      return
    }
    if (id?.includes(ActionName.ARCHIVED) && !this.isArchiveContextMenuFirstLevel(id)) {
      this.onInvoicesArchivedClick(args)
      return
    }
    if (id === ActionName.DOWNLOAD_B2B_CERTIFICATE) {
      this.downloadB2BCertificate()
      return
    }
  }

  formatDataPaginated(data: any) {
    const items = formatFieldsTypeDateEPOCH(data.data)
    const gridResult: DataResult = { result: items, count: parseInt(data.pagination.totalRecs.toString(), 10) }
    return gridResult
  }

  async gridActionChange(serverSideParams: string) {
    const { page, pageSize, columnOrder, search, filter } = JSON.parse(serverSideParams)
    this.gridFilters = {
      ...this.filters,
      page,
      pageSize,
      columnOrder,
      filter,
      search
    }
    await this.fetchGridItems()
    await this.fetchInvoiceTotals()
  }

  async gridExportData(serverSideParams: any) {
    const ssp = JSON.parse(serverSideParams)
    ssp['archived'] = this.archivedSelectedOption
    ssp['paginated'] = true

    await this.fetchCustomerInvoiceList(ssp)
    this.serverSideExportData = this.formatData(this.billingData)
  }

  onCellClicked() {
    trackEvent(TrackerEvents.EDIT_INVOICE)
  }

  downloadB2BCertificate() {
    const pdfPath = `${process.env.BASE_URL}documents/declaracion_responsable_b2brouter_sif.pdf`

    const link = document.createElement('a')
    link.href = pdfPath
    link.download = 'declaracion_responsable_b2brouter_sif.pdf'
    link.target = '_blank'

    document.body.appendChild(link)
    link.click()
    link.remove()
  }
}
</script>
